// eslint-disable-next-line import/no-cycle
import LoopService from '@/services/loop';
import ErrorHandler from '@/tools/errorHandler';
import { i18n } from '@/boot/i18n';
import { TEMPLATE_TYPES } from '@/consts/template';
import { MAX_LENGTH, MIN_LENGTH, REQUIRED } from '../validator/methods';
import { getLoopDefaultState } from './loopState';
import * as types from './mutationTypes';

const LoopServiceEntity = new LoopService();

const validation = () => ({
  name: {
    field: 'name',
    validations: [
      { method: REQUIRED, message: i18n.t('general.rules.required') },
      { method: MAX_LENGTH, value: 57, message: `${i18n.t('general.rules.max_length')} 56` },
    ],
  },
  teams: {
    field: 'teams',
    validations: [{ method: MIN_LENGTH, value: 1, message: i18n.t('general.rules.required') }],
  },
  senderName: {
    field: 'sender_name',
    validations: [{ method: REQUIRED, message: i18n.t('general.rules.required') }],
  },
  priority: {
    field: 'priority',
    validations: [{ method: REQUIRED, message: i18n.t('general.rules.required') }],
  },
});

export default {
  initState({ rootState }) {
    const { uuid } = rootState.organization.organization;
    LoopServiceEntity.setOrganizationUUID(uuid);
  },
  clearState({ commit, dispatch }) {
    commit(types.CLEAR_LOOP_STATE);
    dispatch('validator/clearValidator', null, { root: true });
  },
  clearLoop({ commit }) {
    commit(types.CLEAR_LOOP);
  },
  initNewState({ dispatch, rootState }) {
    const { uuid } = rootState.organization.organization;
    LoopServiceEntity.setOrganizationUUID(uuid);
    dispatch('interaction/setInteractions', [], { root: true });
    dispatch('setLoop', { loop: getLoopDefaultState() });
    const validator = validation();
    dispatch('validator/addValidator', validator.name, { root: true });
    dispatch('validator/addValidator', validator.senderName, { root: true });
    dispatch('validator/addValidator', validator.teams, { root: true });
    dispatch('validator/addValidator', validator.priority, { root: true });
    dispatch('setSenderName', rootState.organization.organization.name);
  },
  clearNewState({ dispatch, commit }) {
    dispatch('interaction/clearInteractions', null, { root: true });
    dispatch('team/clearTeams', null, { root: true });
    dispatch('template/clearTemplates', null, { root: true });
    dispatch('validator/clearValidator', null, { root: true });
    commit(types.CLEAR_LOOP);
  },
  clearQueryParams({ commit }) {
    commit(types.CLEAR_QUERY_PARAMS);
  },
  async getLoops({ dispatch, commit }, params) {
    commit(types.SET_QUERY_PARAMS, { params });
    await dispatch('getAll');
  },
  async setLoop({ commit }, { loop }) {
    commit(types.SET_LOOP, { loop });
  },
  async getAll({ dispatch, commit, state }) {
    try {
      const { queryParams } = state;
      const { data: responseData } = (await LoopServiceEntity.getAll({ ...queryParams })) || {};
      const { data } = responseData || {};
      const loops = data && data.loops ? data.loops : [];
      const totalRows = data && data.total_rows ? data.total_rows : 0;
      commit(types.SET_LOOPS, { loops });
      commit(types.SET_QUERY_PARAMS, {
        params: {
          ...queryParams,
          rowsNumber: totalRows || 0,
        },
      });
    } catch (error) {
      dispatch('ui/showToastError', ErrorHandler.getErrorMessage(error), { root: true });
    }
  },
  async getOne({ dispatch }, uuid) {
    try {
      const { data: responseData } = (await LoopServiceEntity.getOne(uuid)) || {};
      const { data } = responseData || {};
      const loop = data || {};
      dispatch('setLoop', { loop });
    } catch (error) {
      dispatch('ui/showToastError', ErrorHandler.getErrorMessage(error), { root: true });
    }
  },
  async countNewDealts({ dispatch, commit }, loop) {
    try {
      const { data: responseData } = (await LoopServiceEntity.countInteractions(loop.uuid)) || {};
      const { data = 0 } = responseData || {};
      commit(types.SET_LOOP_INTERACTIONS, { loopInteractions: data });
      dispatch('setLoop', { loop });
    } catch (error) {
      dispatch('ui/showToastError', ErrorHandler.getErrorMessage(error), { root: true });
    }
  },
  async updateLoopDealts({ dispatch, state }) {
    const { loop } = state;
    try {
      await LoopServiceEntity.updateLoopDealts(loop.uuid);
      dispatch('ui/showToastSuccess', i18n.t('loop.messages.update_success', { loopName: loop.name }), { root: true });
    } catch (error) {
      dispatch('ui/showToastError', ErrorHandler.getErrorMessage(error), { root: true });
    }
  },
  async updateLoop({ dispatch, state }) {
    try {
      const { loop } = state;
      await LoopServiceEntity.update(loop);
      return true;
    } catch (error) {
      dispatch('ui/showToastError', ErrorHandler.getErrorMessage(error), { root: true });
      return false;
    }
  },
  async createLoop({ dispatch, state }) {
    try {
      const { loop } = state;
      await LoopServiceEntity.create(loop);
      return true;
    } catch (error) {
      dispatch('ui/showToastError', ErrorHandler.getErrorMessage(error), { root: true });
      return false;
    }
  },
  async getIndicators({ dispatch, commit }, { loopUUID, filters }) {
    try {
      const { data: responseData } = (await LoopServiceEntity.getIndicators(loopUUID, filters)) || {};
      const { data = {} } = responseData || {};
      commit(types.SET_INDICATORS, { indicators: data });
    } catch (error) {
      dispatch('ui/showToastError', ErrorHandler.getErrorMessage(error), { root: true });
    }
  },
  async save({ dispatch, state }) {
    const valid = await dispatch('validate');
    if (!valid) {
      return false;
    }
    const { loop } = state;
    if (loop.uuid) {
      return dispatch('updateLoop');
    }
    return dispatch('createLoop');
  },
  setName({ commit, dispatch }, name) {
    commit(types.SET_NAME, { name });
    dispatch('validator/checkValidation', { field: 'name', value: name }, { root: true });
  },
  setSenderName({ commit, dispatch }, senderName) {
    commit(types.SET_SENDER_NAME, { senderName });
    dispatch('validator/checkValidation', { field: 'sender_name', value: senderName }, { root: true });
  },
  setTeams({ commit, dispatch }, teams) {
    commit(types.SET_TEAMS, { teams });
    dispatch('validator/checkValidation', { field: 'teams', value: teams }, { root: true });
  },
  setPriority({ commit, dispatch }, priority) {
    commit(types.SET_PRIORITY, { priority });
    dispatch('validator/checkValidation', { field: 'priority', value: priority }, { root: true });
  },
  setFinishTemplate({ commit }, templateUUID) {
    commit(types.SET_FINISH_LOOP_TEMPLATE, { templateUUID });
  },
  setSendFinishEmail({ commit }, sendFinishEmail) {
    commit(types.SET_SEND_FINISH_EMAIL, { sendFinishEmail });
  },
  setAuto({ commit }, auto) {
    commit(types.SET_AUTO, { auto });
  },
  setSegment({ commit }, segmentUUID) {
    commit(types.SET_SEGMENT, { segmentUUID });
  },
  validate({ dispatch, rootState, state }) {
    dispatch('validator/validate', { field: 'name', value: state.loop.name }, { root: true });
    dispatch('validator/validate', { field: 'sender_name', value: state.loop.sender_name }, { root: true });
    dispatch('validator/validate', { field: 'teams', value: state.loop.teams }, { root: true });
    dispatch('validator/validate', { field: 'priority', value: state.loop.priority }, { root: true });

    return rootState.validator.errors.length === 0;
  },
  getDefaultMessageTemplates({ dispatch }, templates) {
    let templateUUID = null;
    if (templates) {
      templates.forEach((template) => {
        if (template.type === TEMPLATE_TYPES.FINISH_LOOP && template.is_default) {
          templateUUID = template.uuid;
        }
      });
    }
    dispatch('setFinishTemplate', templateUUID);
  },
};
